import React, { useMemo } from 'react';
import { Card, Skeleton, theme } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { useTheme } from '../../../contexts/ui/ThemeContext';
import { EThemeNames } from '../../../../domain/types/themePresets';
import { CATEGORICAL_COLORS } from './chartColors';
import type { OverviewBucket } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface ActivityChartProps {
  buckets: OverviewBucket[];
  status: ContextStatus;
}

const ONE_DAY_MS = 86_400_000;
// À partir de deux semaines de buckets journaliers, la courbe passe du taux de
// décroché à une tendance du volume : une moyenne glissante sur 7 jours, qui
// absorbe le creux du week-end au lieu de le dessiner chaque semaine.
const TREND_MIN_DAYS = 14;
const TREND_WINDOW_DAYS = 7;
const TREND_SERIES_NAME = 'Tendance (moy. 7 j)';
const ANSWER_RATE_SERIES_NAME = 'Taux de décroché';

// Le graphe unique qui remplace les quatre graphes de l'ancien tableau de
// bord : barres empilées entrant/sortant/en défaut, et en ligne soit le taux
// de décroché sur un axe secondaire, soit la tendance du volume sur l'axe des
// appels (voir TREND_MIN_DAYS).
const ActivityChart: React.FC<ActivityChartProps> = ({ buckets, status }) => {
  const { token } = useToken();
  const { themeName } = useTheme();
  const isDark = themeName === EThemeNames.LEEDFLOW_DARK;

  // Le pas (horaire sous 48h, journalier au-delà) est une décision du
  // backend (backend/src/helpers/leedflow/overview-model.ts). Le front la
  // déduit de l'écart entre deux buckets consécutifs plutôt que de
  // redéclarer le seuil de 48h, pour ne jamais faire diverger les deux
  // copies de la règle.
  const stepMs = buckets.length > 1 ? buckets[1].timestamp - buckets[0].timestamp : ONE_DAY_MS;
  const isHourly = stepMs < ONE_DAY_MS;
  const showTrend = !isHourly && buckets.length >= TREND_MIN_DAYS;

  const categories = useMemo(
    () => buckets.map((bucket) => dayjs(bucket.timestamp).format(isHourly ? 'HH:mm' : 'DD/MM')),
    [buckets, isHourly],
  );

  // `inbound + outbound` ne vaut pas `handled` : un appel à direction
  // inconnue compte dans `handled` mais dans aucune des deux (voir
  // OverviewBucket). Ces deux séries restent nommées pour ce qu'elles sont
  // — Entrants / Sortants — jamais présentées comme un total d'appels.
  const inboundSeries = useMemo(() => buckets.map((bucket) => bucket.inbound), [buckets]);
  const outboundSeries = useMemo(() => buckets.map((bucket) => bucket.outbound), [buckets]);

  // Empilés au-dessus des deux autres, jamais confondus avec eux : un appel en
  // défaut n'est ni entrant ni sortant, il n'a pas abouti. Le montrer ici évite
  // qu'il disparaisse du tableau de bord et ne soit jamais réparé.
  const faultySeries = useMemo(() => buckets.map((bucket) => bucket.faulty), [buckets]);

  // Taux de décroché par bucket, division par zéro gardée. `null` plutôt que
  // `0` sur un bucket sans appel décroché ni manqué : un `0` affirmerait
  // « 0 % de décroché » sur une période sans trafic, ce qui est faux. `null`
  // laisse ApexCharts sauter le point (trou dans la courbe), ce qui raconte
  // la vérité — pas de donnée — plutôt qu'une fausse valeur.
  const answerRateSeries = useMemo(
    () =>
      buckets.map((bucket) => {
        const denominator = bucket.answered + bucket.missed;
        return denominator > 0 ? (bucket.answered / denominator) * 100 : null;
      }),
    [buckets],
  );

  // Moyenne glissante de la hauteur des barres (entrants + sortants + en
  // défaut), pour que la tendance se lise contre ce qui est dessiné. `null`
  // sur les six premiers jours : une moyenne sur une fenêtre incomplète
  // ferait démarrer la courbe sur une valeur qui n'est pas comparable au reste.
  const trendSeries = useMemo(() => {
    const totals = buckets.map((bucket) => bucket.inbound + bucket.outbound + bucket.faulty);
    return totals.map((_, index) => {
      if (index < TREND_WINDOW_DAYS - 1) return null;
      const window = totals.slice(index - TREND_WINDOW_DAYS + 1, index + 1);
      return window.reduce((sum, value) => sum + value, 0) / TREND_WINDOW_DAYS;
    });
  }, [buckets]);

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        stacked: true,
        // Sans ça, ApexCharts empile aussi la ligne sur les barres et compte
        // ses valeurs dans le maximum de l'axe partagé.
        stackOnlyBar: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        background: 'transparent',
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: 'end',
          columnWidth: '60%',
        },
      },
      stroke: {
        width: [0, 0, 0, 3],
        curve: 'smooth',
      },
      markers: {
        size: [0, 0, 0, 3],
      },
      // Entrants et Sortants en deux variantes du vert Leedflow, le rouge
      // réservé aux appels en défaut. La courbe (taux ou tendance) passe en
      // couleur de texte : verte, elle se serait confondue avec les deux
      // barres vertes qu'elle traverse.
      colors: [CATEGORICAL_COLORS[0], CATEGORICAL_COLORS[1], token.colorError, token.colorText],
      grid: {
        borderColor: token.colorBorderSecondary,
        strokeDashArray: 4,
      },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: token.colorTextSecondary, fontSize: '12px' } },
      },
      yaxis: [
        {
          // Axe unique partagé par les trois séries empilées. Sur un graphe
          // `stacked`, ApexCharts calcule le maximum de chaque axe
          // uniquement à partir des séries qui lui sont mappées
          // (`setSeriesYAxisMappings` puis `scaleMultipleYAxes` dans
          // apexcharts.esm.js) : avec des objets `yaxis` distincts, chaque
          // série était mise à l'échelle sur son propre maximum, donc dessinée
          // avec une échelle de pixels différente dans une même barre — la pile
          // ne représentait plus rien de comparable dès que les volumes
          // divergent (cas normal en téléphonie). Lister les trois séries ici
          // leur fait partager le même axe, donc le même maximum.
          // La tendance est un nombre d'appels : elle partage cet axe.
          seriesName: showTrend
            ? ['Entrants', 'Sortants', 'En défaut', TREND_SERIES_NAME]
            : ['Entrants', 'Sortants', 'En défaut'],
          min: 0,
          title: { text: 'Appels', style: { color: token.colorTextSecondary } },
          labels: {
            style: { colors: token.colorTextSecondary },
            // Sans ce formateur, ApexCharts rend ses graduations calculées en
            // flottant telles quelles (« 120.00000000000000 ») : un compte
            // d'appels est un entier.
            formatter: (value: number) => `${Math.round(value)}`,
          },
        },
        ...(showTrend
          ? []
          : [
              {
                seriesName: ANSWER_RATE_SERIES_NAME,
                opposite: true,
                min: 0,
                max: 100,
                title: { text: 'Taux de décroché', style: { color: token.colorTextSecondary } },
                labels: {
                  style: { colors: token.colorTextSecondary },
                  formatter: (value: number) => `${Math.round(value)}%`,
                },
              },
            ]),
      ],
      legend: {
        position: 'top',
        labels: { colors: token.colorTextSecondary },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: isDark ? 'dark' : 'light',
        y: [
          { formatter: (value: number) => `${value}` },
          { formatter: (value: number) => `${value}` },
          { formatter: (value: number) => `${value}` },
          {
            formatter: (value: number | null) =>
              value === null
                ? 'Aucune donnée'
                : showTrend
                ? `${Math.round(value)}`
                : `${value.toFixed(1)}%`,
          },
        ],
      },
      fill: { opacity: 1 },
    }),
    [categories, token, isDark, showTrend],
  );

  const series = useMemo(
    () => [
      { name: 'Entrants', type: 'column', data: inboundSeries },
      { name: 'Sortants', type: 'column', data: outboundSeries },
      { name: 'En défaut', type: 'column', data: faultySeries },
      showTrend
        ? { name: TREND_SERIES_NAME, type: 'line', data: trendSeries }
        : { name: ANSWER_RATE_SERIES_NAME, type: 'line', data: answerRateSeries },
    ],
    [inboundSeries, outboundSeries, faultySeries, showTrend, trendSeries, answerRateSeries],
  );

  return (
    <Card title="Appels">
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <ReactApexChart options={chartOptions} series={series} type="line" height={320} />
      )}
    </Card>
  );
};

export default ActivityChart;
